'use client';

/**
 * Film's product analytics — thin helpers over the fleet taxonomy (`@owlcat/app-shell`
 * FleetEvents / useTrack; consent-gated + prod-only inside the provider). Emit only AFTER an
 * action succeeded, and only ids / enums / counts — never prompts, names or emails.
 *
 * One-shot events use sessionStorage guards (a reload or RSC refresh must not double-count):
 *   • signed_in          — once per browser session, on the first authed product render
 *   • film_produce_completed — once per film, when the finished film is on screen
 */

import { useEffect, useRef } from 'react';
import { useTrack } from '@owlcat/app-shell';

export { useTrack };

export { filmTier } from '@/lib/film-tier';

/** Compare a server-action error against the translated "not enough allowance" text. */
export function isInsufficientMessage(message: string | null | undefined, insufficientText: string): boolean {
  return !!message && message === insufficientText;
}

function onceInSession(key: string): boolean {
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return true; // storage blocked → emit (never suppress silently forever)
  }
}

/** The TMA auto-login records how the session was minted so signed_in can say `miniapp`. */
export const SIGNIN_METHOD_KEY = 'film-signin-method';

export function SignedInTracker() {
  const track = useTrack();
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    if (!onceInSession('film-signed-in')) return;
    let method: 'sso' | 'miniapp' = 'sso';
    try {
      if (sessionStorage.getItem(SIGNIN_METHOD_KEY) === 'miniapp') method = 'miniapp';
      sessionStorage.removeItem(SIGNIN_METHOD_KEY);
    } catch { /* ignore */ }
    track('signed_in', { provider: 'sso', method });
  }, [track]);
  return null;
}

export function ProduceCompletedTracker({ filmId, tier, seconds }: { filmId: string; tier: string; seconds: number }) {
  const track = useTrack();
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    if (!onceInSession(`film-produced-${filmId}`)) return;
    track('film_produce_completed', { film_id: filmId, tier, seconds: Math.round(seconds) });
  }, [track, filmId, tier, seconds]);
  return null;
}
